import React, { useState, useMemo, useRef, useEffect    } from "react";
import jsPDF from "jspdf";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
} from "chart.js";
import "./App.css";
import logo from "./assets/vgd-logo.png";
import logoBlanc from "./assets/vgd-logo-blanc.png"
import Racklight from "./assets/Racklight.png"
import Tuborack from "./assets/Tuborack.png"
import XTL from "./assets/XTL.png"


const lampImages = {
  Racklight: Racklight,
  Tuborack: Tuborack,
  XTL: XTL
};


ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale);

const planck = (wavelength_nm, T) => {
  const h = 6.626e-34;
  const c = 3e8;
  const k = 1.381e-23;

  const lambda = wavelength_nm * 1e-9;

  return (2 * h * c ** 2) /
    (lambda ** 5 * (Math.exp((h * c) / (lambda * k * T)) - 1));
};


const whiteSpectra = require('./data/whiteSpectra.json'); // ou importer depuis un fichier JSON

const predefinedRanges = [
  { label: "280", min: 270, max: 290 },
  { label: "290", min: 280, max: 300 },
  { label: "330", min: 320, max: 340 },
  { label: "360", min: 350, max: 370 },
  { label: "385", min: 375, max: 395 },
  { label: "410", min: 400, max: 420 },
  { label: "430", min: 420, max: 440 },
  { label: "450", min: 440, max: 460 },
  { label: "480", min: 470, max: 490 },
  { label: "520", min: 510, max: 530 },
  { label: "630", min: 620, max: 640 },
  { label: "660", min: 650, max: 670 },
  { label: "730", min: 720, max: 740 },
];
const efficacites = {
  "280": 0.5,
  "290": 0.6,
  "330": 0.7,
  "360": 1.6,
  "385": 2.2,
  "410": 3.1,
  "430": 2.2,
  "450": 3.4,
  "480": 2.5,
  "520": 2.4,
  "630": 3.1,
  "660": 4.2,
  "730": 2.9,
  "Blanc 5000K": 2.5,//1000.7
};

// Facteurs utilisés uniquement pour l'affichage du graphique (pas pour le PPFD).
// Permet de garder la courbe du blanc visible sans fausser le calcul physique.
const graphScale = {
  "280": 0.5,
  "290": 0.6,
  "330": 0.7,
  "360": 1.6,
  "385": 2.2,
  "410": 3.1,
  "430": 2.2,
  "450": 3.4,
  "480": 2.5,
  "520": 2.4,
  "630": 3.1,
  "660": 4.2,
  "730": 2.9,
  "Blanc 5000K": 1000.7,
};

function App() {

  const chartRefTop = useRef(null);
  const chartRefRight = useRef(null);
  const [distance, setDistance] = useState("");
  const [selectedLamp, setSelectedLamp] = useState("Racklight");
  const [userEmail, setUserEmail] = useState("");      // <-- Nouvel état email
  const [userRequest, setUserRequest] = useState("");  // <-- Nouvel état demande
 

  const [emailStatus, setEmailStatus] = useState(null); // null | "success" | "error"
  const [emailMessage, setEmailMessage] = useState(""); // texte à afficher

  const [isZoomed, setIsZoomed] = useState(false);

  const [spectra, setSpectra] = useState([{ rangeLabel: "280", intensity: 50, selected: false }]);

  const [nbLuminaires, setNbLuminaires] = useState(1);
  const [surface, setSurface] = useState(1);

  const [exportFormat, setExportFormat] = useState("png"); // png par défaut

  // Ajouter une nouvelle plage
  const addSpectrum = () => {
    // Trouver toutes les plages déjà utilisées
    const usedLabels = spectra.map(s => s.rangeLabel);

    // Trouver la première plage disponible
    const availableRange = predefinedRanges.find(r => !usedLabels.includes(r.label));

    if (!availableRange) {
      alert("Toutes les plages sont déjà utilisées !");
      return;
    }

    // Ajouter une nouvelle ligne avec la première plage libre
    setSpectra([...spectra, { rangeLabel: availableRange.label, intensity: 50, selected: false }]);
  };
  const removeSelected = () => setSpectra(spectra.filter(s => !s.selected));

  const handleChange = (index, field, value) => {
    const newSpectra = [...spectra];
    if (field === "selected") {
      newSpectra[index].selected = value;
    } else {
      newSpectra[index][field] = value;
    }
    setSpectra(newSpectra);
  };



  // Export JSON
  const exportJSON = () => {
    const data = {
      spectra: spectra.map(s => ({
        rangeLabel: s.rangeLabel,
        intensity: Number(s.intensity)
      })),
      distance,
      selectedLamp
    };

    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "spectre.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Import JSON
  const importJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);

        if (data.spectra) {
          // On ne garde que rangeLabel et intensity, et on ajoute selected=false
          const newSpectra = data.spectra.map(s => ({
            rangeLabel: s.rangeLabel, // valeur par défaut si absent
            intensity: s.intensity,
            selected: false
          }));
          setSpectra(newSpectra);
        }

        if (data.distance !== undefined) setDistance(data.distance);
        if (data.selectedLamp) setSelectedLamp(data.selectedLamp);
      } catch (err) {
        alert("Fichier invalide !");
        console.error(err);
      }
    };
    reader.readAsText(file);
  };

  // Génération spectre
  const wavelengthToRGB = (w) => {
    let R = 0, G = 0, B = 0;
    //if (w >= 220 && w < 380) {  R = -(w-440)/60; B=1; } 
    if (w < 380) { R = 0; G = 0; B = 0;}   
    else if (w >= 380 && w < 440) { R = -(w-440)/60; B=1; }
    else if (w >= 440 && w < 490) { G=(w-440)/50; B=1; }
    else if (w >= 490 && w < 510) { G=1; B=-(w-510)/20; }
    else if (w >= 510 && w < 580) { R=(w-510)/70; G=1; }
    else if (w >= 580 && w < 645) { R=1; G=-(w-645)/65; }
    else if (w >= 645 && w < 780) { R=1; }
    //else if (w > 780 && w <= 950) { R=1; G=0; B=0;  }*
    else if (w >= 780) { R = 0; G = 0; B = 0; }
    const gamma=0.8;
    R=Math.pow(R,gamma)*255; G=Math.pow(G,gamma)*255; B=Math.pow(B,gamma)*255;
    return `rgb(${Math.round(R)},${Math.round(G)},${Math.round(B)})`;
  };

 const { wavelengths, combinedValues } = useMemo(() => {
  const wavelengths = [];
  const combinedValues = [];

  const step = 2;

  for (let nm = 220; nm <= 800; nm += step) {
    wavelengths.push(nm);
    let total = 0;

    spectra.forEach(s => {
      const white = whiteSpectra.find(w => w.label === s.rangeLabel);
      if (white) {
        // trouver le point le plus proche dans white.data
        const closestPoint = white.data.reduce((prev, curr) =>
          Math.abs(curr.x - nm) < Math.abs(prev.x - nm) ? curr : prev
        , white.data[0]);

        const eff = graphScale[s.rangeLabel] ?? 1;
        total += (closestPoint?.y ?? 0) * Number(s.intensity) * eff / 100;
      } else {
        const range = predefinedRanges.find(r => r.label === s.rangeLabel);
        if (!range || s.intensity === "") return;

        const intensity = Number(s.intensity);
        const center = (range.min + range.max) / 2;
        const width = (range.max - range.min) / 2;

        const eff = graphScale[s.rangeLabel] ?? 1;
        total += intensity * eff * Math.exp(
          -Math.pow(nm - center, 2) / (2 * width * width)
        );
      }
    });

    combinedValues.push(total);
  }

  return { wavelengths, combinedValues };
}, [spectra]);  // Calcul du max dynamique avec minimum à 100
  const realMax = Math.max(...combinedValues, 0);
  const dynamicMax = Math.max(100, Math.ceil(realMax * 1.1)); 
  // +10% pour laisser un peu d'espace en haut

const xLabels = useMemo(() => {
  return wavelengths.map(nm =>
    (nm % 100 === 0 || nm === 220 || nm === 800) ? nm : ""
  );
}, [wavelengths]);

 const chartData = useMemo(() => ({
  labels: xLabels,
  datasets: [
    {
      label: "Spectre lumineux",
      data: combinedValues,

      borderColor: (context) => {
        const chart = context.chart;
        const ctx = chart.ctx;
        const area = chart.chartArea;
        if (!area) return "blue";

        const grad = ctx.createLinearGradient(
          area.left,
          0,
          area.right,
          0
        );

        for (let i = 0; i < wavelengths.length; i++) {
          grad.addColorStop(
            i / (wavelengths.length - 1),
            wavelengthToRGB(wavelengths[i])
          );
        }

        return grad;
      },

      backgroundColor: "rgba(0,0,0,0)", // ⚡ on enlève le gradient fill (gros gain perf)

      fill: false,
      tension: 0.4,
      pointRadius: 0,
      borderWidth: 2,
    },
  ],
}),  [combinedValues, wavelengths, xLabels]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { title: { display: true, text: "Longueur d'onde (nm)", font: { style: 'italic', size: 14 } },
        ticks: { callback: function(value, index) { return this.getLabelForValue(index) || null; }, maxRotation:0, minRotation:0 } },
      y: { title: { display: true, text: "µmol/s", font: { style: 'italic', size: 14 } },
        min:0, max:dynamicMax, ticks:{ stepSize:dynamicMax / 0  } }
    }
  };

  const logoImageRef = useRef(new Image());
useEffect(() => {
  logoImageRef.current.src = logoBlanc;
}, []);

const downloadChart = () => {
  if (!chartRefRight.current) return;

  const chartInstance = chartRefRight.current;
  const chartCanvas = chartInstance.canvas;

  // Canvas temporaire
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = chartCanvas.width;
  tempCanvas.height = chartCanvas.height;
  const tempCtx = tempCanvas.getContext("2d");

   // === FOND VERT ===
  tempCtx.fillStyle = "rgb(44, 60, 45)";
  tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

  // Copier le graphique
  tempCtx.drawImage(chartCanvas, 0, 0);

  // Ajouter le logo au centre
  const logo = logoImageRef.current;
  const logoWidth = tempCanvas.width * 0.5;
  const logoHeight = (logoWidth / logo.width) * logo.height;
  const x = (tempCanvas.width - logoWidth) / 2;
  const y = (tempCanvas.height - logoHeight) / 2;

  tempCtx.globalAlpha = 0.1;
  tempCtx.drawImage(logo, x, y, logoWidth, logoHeight);
  tempCtx.globalAlpha = 1;

  const imageData = tempCanvas.toDataURL("image/png");

  // ===== EXPORT PNG =====
  if (exportFormat === "png") {
    const link = document.createElement("a");
    link.href = imageData;
    link.download = "spectre.png";
    link.click();
  }

 // ===== EXPORT PDF =====
if (exportFormat === "pdf") {

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Garder les proportions
  const ratio = Math.min(
    pageWidth / tempCanvas.width,
    pageHeight / tempCanvas.height
  );

  const imgWidth = tempCanvas.width * ratio;
  const imgHeight = tempCanvas.height * ratio;

  const xOffset = (pageWidth - imgWidth) / 2;
  const yOffset = (pageHeight - imgHeight) / 2;

  pdf.addImage(
    imageData,
    "PNG",
    xOffset,
    yOffset,
    imgWidth,
    imgHeight
  );

  pdf.save("spectre.pdf");
}
};


  // Fonction pour envoyer email via mailto:
  const sendEmail = async () => {
  if (!userEmail || !userRequest) {
    setEmailStatus("error");
    setEmailMessage("Merci de remplir votre email et votre demande.");
    return;
  }

  const pageData = {
    spectra: spectra.map(s => ({
      rangeLabel: s.rangeLabel,
      intensity: Number(s.intensity) // convertir en nombre si besoin
    })),
    distance,
    selectedLamp
  };

  try {
    const res = await fetch("http://localhost:5000/send-mail", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: userEmail,
        message: userRequest,
        pageData
      }),
    });

    const data = await res.json();
    if (data.success) {
      setEmailStatus("success");
      setEmailMessage("Mail envoyé avec succès !");
      setUserEmail("");
      setUserRequest("");
    } else {
      setEmailStatus("error");
      setEmailMessage("Erreur lors de l'envoi du mail.");
    }
  } catch (err) {
    setEmailStatus("error");
    setEmailMessage("Erreur lors de l'envoi du mail.");
    console.error(err);
  }

  // Faire disparaître le message après 5 secondes
  setTimeout(() => {
    setEmailStatus(null);
    setEmailMessage("");
  }, 5000);
};

const { avgR, avgG, avgB } = useMemo(() => {
  let sumR = 0, sumG = 0, sumB = 0;
  let count = 0;

  wavelengths.forEach((w, i) => {
    const rgb = wavelengthToRGB(w) // retourne "rgb(R,G,B)"
      .match(/\d+/g)
      .map(Number); // [R,G,B]

    sumR += rgb[0] * combinedValues[i]; // pondération par l’intensité
    sumG += rgb[1] * combinedValues[i];
    sumB += rgb[2] * combinedValues[i];
    count += combinedValues[i];
  });

  if (count === 0) return { avgR: 0, avgG: 0, avgB: 0 };

  return {
    avgR: Math.round(sumR / count),
    avgG: Math.round(sumG / count),
    avgB: Math.round(sumB / count),
  };
}, [wavelengths, combinedValues]);
const rgbToHex = (r, g, b) => {
  const toHex = (v) => v.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};


  return (
    
    <div className="app-container">
      
      <div className="app-header">
        <p>Simulateur de Spectre</p> 
        <img src={logo} alt="Logo VGD"/>
      </div> 
      <div className="app-content">
        <div className="flex-container">
          <div className="left-side">
            <div className="import-content">
              <p>vous possedez déjà une configuration ?</p>
            <label className="import-button">
                Importer une <span>configuration</span>
                <input type="file" accept=".json" onChange={importJSON} style={{ display: "none" }}   />
              </label>
            </div>

            <div className="top-side" >
              <img src={logoBlanc} alt="copyright" className="img-copyright" />
              <Line ref={chartRefTop} data={chartData} options={chartOptions} />
            </div>

             <div className="luminaire-surface-inputs">
               <h2>Informations additionnelles</h2>
                <label>
                  Nombre de luminaires :
                  <input
                    type="number"
                    value={nbLuminaires}
                    onChange={e => setNbLuminaires(e.target.value)}
                  />
                </label>
                <label>
                  Surface (m²) :
                  <input
                    type="number"
                    value={surface}
                    onChange={e => setSurface(e.target.value)}
                  />
                </label>
              </div>

            <div className="conteneur-tableau">

              <h2>Configurer son spectre<span style={{ color: 'red' }}>*</span></h2>

            

              <div className="tableau">
                <div className="inputs-header">
                  <span></span>
                  <span>Spectre <br />(nm)</span>
                  <span>Watts <br /><br /></span>
                  <span>µmol/m²/s <br /><br /></span>
                </div>

                {spectra.map((s,index)=>(
                <div key={index} className="spectrum-inputs">
                    <input 
                      type="checkbox" 
                      checked={s.selected} 
                      onChange={e => handleChange(index,"selected",e.target.checked)} 
                    />

                   <select
  value={s.rangeLabel}
  onChange={e => handleChange(index, "rangeLabel", e.target.value)}
>
  {predefinedRanges
    .filter(r => !spectra.some((sp, i) => i !== index && sp.rangeLabel === r.label))
    .map(r => (
      <option key={r.label} value={r.label}>
        {r.label} ({r.min}-{r.max} nm)
      </option>
    ))
  }
  {whiteSpectra
    .filter(w => !spectra.some((sp, i) => i !== index && sp.rangeLabel === w.label))
    .map(w => (
      <option key={w.label} value={w.label}>
        {w.label}
      </option>
    ))
  }
</select>

                    <input
                      type="number"
                      value={s.intensity}
                      onChange={e=>handleChange(index,"intensity",e.target.value)}
                    />
                    <span className="ppfd-cell">
                      {(() => {
                        const w = Number(s.intensity) || 0;
                        const ratio = efficacites[s.rangeLabel] ?? 0;
                        const nb = Number(nbLuminaires) || 0;
                        const surf = Number(surface);
                        if (!surf) return "-";
                        return ((w * ratio * nb) / surf).toFixed(2);
                      })()}
                    </span>
                  </div>
                ))}
              </div>

              <div className="ppfd-total">
                Total : {(() => {
                  const nb = Number(nbLuminaires) || 0;
                  const surf = Number(surface);
                  if (!surf) return "-";
                  const total = spectra.reduce((acc, s) => {
                    const w = Number(s.intensity) || 0;
                    const ratio = efficacites[s.rangeLabel] ?? 0;
                    return acc + (w * ratio * nb) / surf;
                  }, 0);
                  return total.toFixed(2);
                })()} µmol/m²/s
              </div>

              <div className="buttons-table">
                <button onClick={addSpectrum}>Ajouter un spectre</button>
                <button onClick={removeSelected}>Supprimer le(s) ligne(s)</button>
              </div>
            </div>
          
            <p style={{ fontStyle: 'italic' }}><span style={{ color: 'red' }}>*</span>
              courbe spectrale indicative et intensité théorique non contractuelle, variable en fonction de la disponibilité des composants et de leur évolution.
            </p>


            <div className="additional-inputs">
              <h2>Informations complémentaires</h2>
              <label className="distance">
                Distance [ lampe → plante ] : 
                <input type="number" value={distance} onChange={e => setDistance(e.target.value)} />
                <span>cm</span>
              </label>

             <label className="light-type">
                Choisir la lampe :

                <select value={selectedLamp} onChange={e => setSelectedLamp(e.target.value)} >
                  {Object.keys(lampImages).map(lamp => (
                    <option key={lamp} value={lamp}>
                      {lamp}
                    </option>
                  ))}
                </select>

                <div className="lamp-preview" onClick={() => setIsZoomed(true)} >
                  <img
                    src={lampImages[selectedLamp]}
                    alt={selectedLamp}
                    className="lamp-image"
                  />
                  <p className="lamp-text"> cliquez pour zoomer.</p>
                </div>
              </label>

              {isZoomed && (
                <div className="lightbox" onClick={() => setIsZoomed(false)}>
                  <img
                    src={lampImages[selectedLamp]}
                    alt={selectedLamp}
                    className="lightbox-image"
                  />
                  <p className="lightbox-text"> cliquez pour dézoomer.</p>
                </div>
              )}
            </div>

            <button onClick={exportJSON} className="export-button">Exporter la configuration</button>
           

            {/* --- Nouveaux champs pour email et demande --- */}
           
          </div>

          <div className="right-side" >
            <img src={logoBlanc} alt="copyright" className="img-copyright" />
            <Line ref={chartRefRight} data={chartData} options={chartOptions} />

              <div className="download">
                <button onClick={downloadChart} class="downloadButton">📥 Télécharger le graphique</button>
                <div className="export-toggle">
                  <button
                    className={exportFormat === "png" ? "active" : ""}
                    onClick={() => setExportFormat("png")}
                  >
                    PNG
                  </button>

                  <button
                    className={exportFormat === "pdf" ? "active" : ""}
                    onClick={() => setExportFormat("pdf")}
                  >
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

              {/*}
       <div className="user-request-section">
          <h2>Envoyer une demande à notre équipe</h2>
          <label>
            Votre email:
            <input type="email" value={userEmail} onChange={e => setUserEmail(e.target.value)} />
          </label>
          <label className="demande">
            Votre demande:
            <textarea value={userRequest} onChange={e => setUserRequest(e.target.value)} rows={4} />
          </label>
          <div className="foot-from-mail">         
            <button onClick={sendEmail}>Envoyer le mail</button>
            <p>Les données créaient ci-dessus seront automatiquement jointes au mail.</p>
          </div>

          {emailStatus && (
            <div className={`email-feedback ${emailStatus}`}>
              {emailMessage}
            </div>
          )}
        </div>
        */}
    </div>
  );
}

export default App;